/**
 * @file guiSlice.ts
 * @description Redux slice for GUI/UI user preferences and app initialization status
 *
 * UI Preferences Architecture:
 * - Redux state provides instant UI updates (session)
 * - localStorage provides browser persistence (offline support)
 * - Backend API provides cross-device sync
 *
 * Data flow on preference change:
 * 1. Redux state updated immediately (UI responds)
 * 2. localStorage updated (browser persistence)
 * 3. API call in background (cross-device sync)
 *
 * Data flow on app load:
 * 1. Load from localStorage first (instant)
 * 2. Fetch from API (may overwrite if newer)
 */

import {
  defaultUiPreferences,
  type FeaturePreferences,
  type UiFeature,
  type UiPreferences,
  type ViewMode
} from "@nexus/types/domain"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"

/**
 * Initialization status for boot tasks
 */
export interface InitStatus {
  token: boolean
  models: boolean
}

/**
 * GUI state - User preferences and UI configuration
 */
export interface GuiState {
  initStatus: InitStatus
  /** Server-synced UI preferences */
  uiPreferences: UiPreferences
  /** Whether preferences have been loaded from server */
  preferencesLoaded: boolean
}

const initialState: GuiState = {
  initStatus: {
    token: false,
    models: false
  },
  uiPreferences: defaultUiPreferences,
  preferencesLoaded: false
}

/**
 * Redux slice for managing UI preferences and initialization status
 */
const guiSlice = createSlice({
  name: "gui",
  initialState,
  reducers: {
    /**
     * Sets the initialization status for a given task key
     */
    setInitStatus(state, action: PayloadAction<{ key: keyof InitStatus; status: boolean }>) {
      state.initStatus[action.payload.key] = action.payload.status
    },

    /**
     * Sets the entire UI preferences object (used when loading from API)
     */
    setUiPreferences(state, action: PayloadAction<UiPreferences>) {
      state.uiPreferences = action.payload
      state.preferencesLoaded = true
    },

    /**
     * Sets the user's favorite/default agent (convenience method)
     */
    setFavoriteAgent(state, action: PayloadAction<string | null>) {
      state.uiPreferences.global = {
        ...state.uiPreferences.global,
        favoriteAgentId: action.payload
      }
    },

    /**
     * Sets the view mode for a specific feature
     */
    setFeatureViewMode(state, action: PayloadAction<{ feature: UiFeature; viewMode: ViewMode }>) {
      const { feature, viewMode } = action.payload
      state.uiPreferences.features = {
        ...state.uiPreferences.features,
        [feature]: {
          ...state.uiPreferences.features?.[feature],
          viewMode
        }
      }
    },

    /**
     * Sets the realtime sync enabled flag
     */
    setRealtimeSyncEnabled(state, action: PayloadAction<boolean>) {
      state.uiPreferences.global = {
        ...state.uiPreferences.global,
        realtimeSyncEnabled: action.payload
      }
    },

    /**
     * Updates preferences for a specific feature
     */
    setFeaturePreferences(
      state,
      action: PayloadAction<{ feature: UiFeature; preferences: Partial<FeaturePreferences> }>
    ) {
      const { feature, preferences } = action.payload
      state.uiPreferences.features = {
        ...state.uiPreferences.features,
        [feature]: {
          ...state.uiPreferences.features?.[feature],
          ...preferences
        }
      }
    }
  }
})

export const {
  setInitStatus,
  setUiPreferences,
  setFavoriteAgent,
  setRealtimeSyncEnabled,
  setFeatureViewMode,
  setFeaturePreferences
} = guiSlice.actions

export default guiSlice.reducer

// ============================================================================
// Selectors
// ============================================================================

type RootState = { gui: GuiState }

/** Select the entire UI preferences object */
export const selectUiPreferences = (state: RootState) => state.gui.uiPreferences

/** Select whether preferences have been loaded from server */
export const selectPreferencesLoaded = (state: RootState) => state.gui.preferencesLoaded

/** Select the user's favorite agent ID */
export const selectFavoriteAgentId = (state: RootState) =>
  state.gui.uiPreferences.global?.favoriteAgentId ?? null

/** Select view mode for a specific feature */
export const selectFeatureViewMode = (state: RootState, feature: UiFeature): ViewMode =>
  state.gui.uiPreferences.features?.[feature]?.viewMode ?? "cards"

/** Select all preferences for a specific feature */
export const selectFeaturePreferences = (
  state: RootState,
  feature: UiFeature
): FeaturePreferences => state.gui.uiPreferences.features?.[feature] ?? { viewMode: "cards" }

/** Select whether realtime sync is enabled (defaults to false) */
export const selectRealtimeSyncEnabled = (state: RootState): boolean =>
  state.gui.uiPreferences.global?.realtimeSyncEnabled ?? false
